/**
 * Keeping the tab's local state honest against the data underneath it.
 *
 * Every lever on this tab is stored by KEY or by INDEX — interns per cluster, out-of-office per
 * cluster, an allocation as `{ from: teamIndex, to: clusterKey }`. All of those can be invalidated
 * by something the user does elsewhere, or by the live portfolio arriving with a different cluster
 * list. `reconcileState` is the one place that repairs it, and every write goes through it.
 *
 * It exists because each of these produced capacity the team did not have:
 *
 *  · Unassigning a lender dropped the debit from their old cluster and left the credit on the
 *    receiver — hours appearing in a cluster that existed nowhere in the company total.
 *  · Moving a lender INTO the cluster they were lending to made the transfer net to zero while the
 *    support row still claimed it was happening.
 *  · Out-of-office FTE outlived the working model that justified it, so a cluster whose recruiters
 *    all went to 0 h could report negative committed capacity.
 *  · Intern allocation beyond the pool was flagged but still counted, the opposite of what the
 *    Cluster Lead Check-In tab does (`allocScale` scales it back precisely so the index only ever
 *    reflects capacity that exists).
 */
import {
  ACTIVITIES,
  CHANGE_LOG_COALESCE_MS,
  CHANGE_LOG_LIMIT,
  RATINGS,
  SEED_TEAM,
  UNASSIGNED,
  seedState,
  type Allocation,
  type CapacityAssumptions,
  type ChangeLogEntry,
  type EffortModel,
  type PrioritisationState,
  type Rating,
  type SupportCommitment,
  type WorkingModel,
} from '../types/rolePrioritisation';
import { clusterFte, internPoolFte, isPlanned } from './rolePrioritisation';

export interface ReconcileNote {
  kind:
    | 'allocation-dropped'
    | 'interns-clamped'
    | 'out-clamped'
    | 'cluster-unknown'
    | 'assumption-clamped';
  detail: string;
}

/**
 * Overhead and buffer are editable, and the pair must leave something behind.
 *
 * The ceiling is on the COMBINED yield rather than each percentage: 95% overhead and 95% buffer are
 * each individually plausible-looking typos that together leave 0.25% of the week, at which point
 * every utilisation is a four-figure number and the tab says nothing. A floor on the yield keeps
 * the arithmetic meaningful and is reported rather than applied silently.
 */
export const MIN_CAPACITY_YIELD = 0.05;
const MAX_WEEK_HOURS = 80;

export interface Reconciled {
  state: PrioritisationState;
  /** What had to be repaired. Surfaced rather than applied silently. */
  notes: ReconcileNote[];
}

/**
 * @param clusterKeys the clusters that currently exist — from the live portfolio when it loaded,
 *                    otherwise the seed. State referring to anything else is not kept.
 */
export function reconcileState(state: PrioritisationState, clusterKeys: string[]): Reconciled {
  const known = new Set(clusterKeys);
  const notes: ReconcileNote[] = [];

  // The assumptions come first: everything below divides by them.
  const cap = saneCap(state.cap, notes);
  const model = saneModel(state.model, notes);

  // A person can only sit in a cluster that exists. Anything else becomes unassigned, which is
  // truthful: their hours belong to no cluster's load.
  const team = state.team.map((p) => {
    if (p.cluster === UNASSIGNED || known.has(p.cluster)) return p;
    notes.push({ kind: 'cluster-unknown', detail: `${p.name} was in ${p.cluster}, which no longer exists` });
    return { ...p, cluster: UNASSIGNED };
  });

  // Out-of-office cannot exceed the FTE actually in the cluster, or committed capacity goes
  // negative. Clamped rather than rejected: the working model is the thing that changed.
  const out: Record<string, number> = {};
  clusterKeys.forEach((key) => {
    const wanted = Math.max(Number(state.out[key]) || 0, 0);
    const ceiling = clusterFte({ ...state, team }.team, key, cap);
    const value = Math.min(wanted, ceiling);
    if (wanted - value > 0.005) {
      notes.push({
        kind: 'out-clamped',
        detail: `${key}: ${wanted} FTE out of office reduced to ${Math.round(value * 100) / 100} — the cluster holds no more`,
      });
    }
    out[key] = value;
  });

  // Interns are drawn from the intern rows the TEAM actually holds — never a typed count, which
  // used to let the tab plan on an intern nobody had added. Allocating more than exists must not
  // credit capacity, so the budget is spent in key order and the remainder is refused.
  const available = Math.max(internPoolFte(team, cap) - (Number(state.poolOut) || 0), 0);
  const interns: Record<string, number> = {};
  let left = available;
  clusterKeys.forEach((key) => {
    const wanted = Math.max(Number(state.interns[key]) || 0, 0);
    const value = Math.min(wanted, Math.max(left, 0));
    if (wanted - value > 0.005) {
      notes.push({
        kind: 'interns-clamped',
        detail: `${key}: ${wanted} intern FTE reduced to ${Math.round(value * 100) / 100} — the team holds ${Math.round(available * 100) / 100}`,
      });
    }
    interns[key] = value;
    left -= value;
  });

  // An allocation is only meaningful while its lender still sits in a cluster OTHER than the one
  // receiving the support.
  const allocations = state.allocations.filter((a) => {
    const person = team[a.from];
    if (!person) {
      notes.push({ kind: 'allocation-dropped', detail: 'a support allocation referred to someone no longer on the team' });
      return false;
    }
    if (!known.has(a.to)) {
      notes.push({ kind: 'allocation-dropped', detail: `${person.name}'s support to ${a.to} — that cluster no longer exists` });
      return false;
    }
    // Someone NOT planned as capacity can lend from anywhere, including their own cluster: their
    // hours sit in no cluster's total, so neither rule below applies to them.
    if (!isPlanned(person)) return Number.isFinite(a.hours) && a.hours > 0;
    if (person.cluster === UNASSIGNED) {
      notes.push({
        kind: 'allocation-dropped',
        detail: `${person.name} carries no cluster, so their ${a.hours} h of support to ${a.to} was removed`,
      });
      return false;
    }
    if (person.cluster === a.to) {
      notes.push({
        kind: 'allocation-dropped',
        detail: `${person.name} now carries ${a.to} directly, so the support arrangement was removed`,
      });
      return false;
    }
    if (!Number.isFinite(a.hours) || a.hours <= 0) return false;
    return true;
  });

  return {
    state: {
      ...state,
      team,
      interns,
      out,
      allocations,
      cap,
      model,
      log: (state.log ?? []).slice(0, CHANGE_LOG_LIMIT),
    },
    notes,
  };
}

/** A contracted week and an overhead/buffer pair that still leave capacity to plan with. */
function saneCap(raw: CapacityAssumptions | undefined, notes: ReconcileNote[]): CapacityAssumptions {
  const seedLike = { fullWeekHours: 40, overheadPct: 20, bufferPct: 10 };
  const src = raw ?? seedLike;
  const week = clamp(num(src.fullWeekHours, seedLike.fullWeekHours), 1, MAX_WEEK_HOURS);
  let overhead = clamp(num(src.overheadPct, seedLike.overheadPct), 0, 100);
  let buffer = clamp(num(src.bufferPct, seedLike.bufferPct), 0, 100);

  if ((1 - overhead / 100) * (1 - buffer / 100) < MIN_CAPACITY_YIELD) {
    notes.push({
      kind: 'assumption-clamped',
      detail: `${overhead}% overhead and ${buffer}% buffer leave almost no week to plan with — reduced to the seeded 20% / 10%`,
    });
    overhead = seedLike.overheadPct;
    buffer = seedLike.bufferPct;
  }
  if (week !== num(src.fullWeekHours, seedLike.fullWeekHours)) {
    notes.push({
      kind: 'assumption-clamped',
      detail: `a full week of ${src.fullWeekHours} h is not a week — set to ${week} h`,
    });
  }
  return { fullWeekHours: week, overheadPct: overhead, bufferPct: buffer };
}

/**
 * Activity hours must be finite and non-negative. A NEGATIVE activity is the one that matters: it
 * would price a role below zero and make demand fall as roles arrive.
 */
function saneModel(raw: EffortModel | undefined, notes: ReconcileNote[]): EffortModel {
  const out = {} as EffortModel;
  RATINGS.forEach((r) => {
    const row = raw?.[r];
    out[r] = ACTIVITIES.reduce(
      (acc, a) => {
        const v = num(row?.[a.id], 0);
        if (v < 0) {
          notes.push({
            kind: 'assumption-clamped',
            detail: `${r} ${a.label.toLowerCase()} cannot be negative — set to 0`,
          });
        }
        acc[a.id] = Math.max(v, 0);
        return acc;
      },
      {} as EffortModel[typeof r]
    );
  });
  return out;
}

const num = (v: unknown, fallback: number) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/**
 * Append an edit to the log, folding it into the previous entry when it is the same person editing
 * the same thing again within `CHANGE_LOG_COALESCE_MS`.
 *
 * `what` is the identity used for that comparison, so it must name the FIELD rather than the value:
 * "Anna Thiel · working model" coalesces across keystrokes, "Anna Thiel 40 h → 20 h" would not.
 */
export function withLogEntry(
  state: PrioritisationState,
  by: string | null | undefined,
  what: string,
  now: Date = new Date()
): PrioritisationState {
  const who = by || 'unknown';
  const log = state.log ?? [];
  const head = log[0];
  const entry: ChangeLogEntry = { at: now.toISOString(), by: who, what };

  if (
    head &&
    head.by === who &&
    head.what === what &&
    now.getTime() - new Date(head.at).getTime() < CHANGE_LOG_COALESCE_MS
  ) {
    return { ...state, log: [entry, ...log.slice(1)] };
  }
  return { ...state, log: [entry, ...log].slice(0, CHANGE_LOG_LIMIT) };
}

/**
 * A commitment runs until the next cluster-lead weekly and then has to be re-cut. Without this the
 * board stayed read-only indefinitely, still presenting an arrangement whose own validity date had
 * passed as though it were in force.
 */
export function commitmentExpired(
  state: PrioritisationState,
  now: Date = new Date()
): boolean {
  const iso = state.commitment?.validUntilIso;
  if (!iso) return false;
  const until = new Date(iso);
  if (Number.isNaN(until.getTime())) return false;
  return now.getTime() > until.getTime();
}

/**
 * A stored scratchpad blob, merged onto the seed rather than trusted wholesale: it predates every
 * later change to the team or the cluster list, and a missing key must fall back to the seed instead
 * of rendering NaN. `reconcileState` then repairs anything that still does not hold.
 */
export function hydrateState(raw: unknown): PrioritisationState {
  const seed = seedState();
  const stored = (raw ?? null) as Partial<PrioritisationState> | null;
  if (!stored || typeof stored !== 'object') return seed;

  // Rows are matched by NAME, not by index, and rows the seed does not have are kept: an added
  // intern is a row of its own, so the old length check dropped both the intern and every
  // working-model edit beside it the moment one was added.
  const team = Array.isArray(stored.team) && stored.team.length
    ? stored.team
        .filter((p): p is WorkingModel => !!p && typeof p.name === 'string')
        .map((p) => {
          const base = SEED_TEAM.find((b) => b.name === p.name);
          return {
            ...(base ?? { name: p.name, role: p.role || 'Recruiter', cluster: UNASSIGNED, hours: 0 }),
            hours: Number.isFinite(Number(p.hours)) ? Number(p.hours) : (base?.hours ?? 0),
            cluster: typeof p.cluster === 'string' ? p.cluster : (base?.cluster ?? UNASSIGNED),
            countsTowardHiring: p.countsTowardHiring ?? base?.countsTowardHiring,
            isIntern: p.isIntern ?? base?.isIntern,
          };
        })
    : seed.team;

  return {
    team,
    interns: stored.interns && typeof stored.interns === 'object' ? stored.interns : seed.interns,
    out: stored.out && typeof stored.out === 'object' ? stored.out : seed.out,
    poolOut: Number(stored.poolOut) || 0,
    // Each ELEMENT is checked, not just the container: a null in this array reaches
    // `reconcileState`, which reads `a.from` off it.
    allocations: Array.isArray(stored.allocations)
      ? stored.allocations.filter(isAllocation).map((a) => ({ ...a, focus: saneFocus(a.focus) }))
      : [],
    added: {
      AAA: Number(stored?.added?.AAA) || 0,
      A: Number(stored?.added?.A) || 0,
      B: Number(stored?.added?.B) || 0,
      C: Number(stored?.added?.C) || 0,
    },
    commitment: isCommitment(stored.commitment) ? stored.commitment : null,
    // Both are validated by reconcileState, which is the only thing that may decide what a sane
    // assumption is — merging a stored blob is not the place for a second opinion.
    model: stored.model ?? seed.model,
    cap: stored.cap ?? seed.cap,
    // A truthy non-STRING `by` used to pass a `&&` check and then crash `ChangeLog`'s
    // `by.includes('@')`. Every field is type-checked, and a bad row is dropped, not repaired:
    // there is nothing to recover from an entry that cannot say who or what.
    log: Array.isArray(stored.log) ? stored.log.filter(isLogEntry) : [],
  };
}

const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** `focus` names the rating the support is aimed at, or '' for unspecified — never free text. */
const saneFocus = (v: unknown): Allocation['focus'] =>
  RATINGS.includes(v as Rating) ? (v as Rating) : '';

function isAllocation(a: unknown): a is Allocation {
  if (!a || typeof a !== 'object') return false;
  const { from, to, hours } = a as Partial<Allocation>;
  return (
    Number.isInteger(from) &&
    (from as number) >= 0 &&
    isStr(to) &&
    Number.isFinite(hours) &&
    (hours as number) > 0
  );
}

function isCommitment(c: unknown): c is SupportCommitment {
  if (!c || typeof c !== 'object') return false;
  const { by, at, validUntil } = c as Partial<SupportCommitment>;
  // `validUntilIso` is deliberately NOT required: a commitment stored before it existed is still a
  // real commitment, and `commitmentExpired` already treats a missing or unparseable date as
  // "not expired" rather than silently unlocking a board someone agreed to.
  return isStr(by) && isStr(at) && isStr(validUntil);
}

function isLogEntry(e: unknown): e is ChangeLogEntry {
  if (!e || typeof e !== 'object') return false;
  const { at, by, what } = e as Partial<ChangeLogEntry>;
  return isStr(at) && isStr(by) && isStr(what);
}

/**
 * What currently differs from the calibrated baseline, named for display.
 *
 * The change log needs this because an EMPTY log does not mean nothing was changed: a blob stored
 * before the log existed can carry edited values with no history at all, and saying "nothing has
 * been changed yet" over an edited model would be a lie told by the audit trail itself.
 */
export function deviationsFromSeed(state: PrioritisationState): string[] {
  const seed = seedState();
  const out: string[] = [];

  const teamChanged = state.team.some((p) => {
    const base = SEED_TEAM.find((b) => b.name === p.name);
    return !base || base.hours !== p.hours || base.cluster !== p.cluster || isPlanned(base) !== isPlanned(p);
  });
  if (teamChanged || state.team.length !== SEED_TEAM.length) out.push('team and working models');

  if (RATINGS.some((r) => ACTIVITIES.some((a) => state.model[r][a.id] !== seed.model[r][a.id]))) {
    out.push('effort model');
  }
  if ((Object.keys(seed.cap) as (keyof typeof seed.cap)[]).some((k) => state.cap[k] !== seed.cap[k])) {
    out.push('capacity assumptions');
  }
  return out;
}
